import React from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import usePortalTarget from 'hooks/usePortalTarget'
import { useSidePanel } from 'hooks'
import { SIDE_PANEL_CONTENT_IDS } from 'cs-kevlar-react/lib/es/SidePanel'
import { IconButton, HeaderBar, Box } from 'cs-kevlar-react'
import { N30 } from 'cs-kevlar-react/lib/es/theming/palette'
import { Close } from 'cs-kevlar-react/lib/es/icons'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sidePanelContent: {
      flexGrow: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
    header: {
      paddingLeft: theme.spacing(8),
      backgroundColor: N30,
    },
    closeButton: {
      padding: theme.spacing(6),
      marginTop: -1 * parseInt(theme.spacing(6)),
      marginBottom: -1 * parseInt(theme.spacing(6)),
    },
  }),
)

interface SidePanelContentProps {
  children: any
  anchor: 'right' | 'bottom'
  onClose: () => void
  title: string
  auditTitle?: string
  isReadyToAudit?: boolean
  isSupportPage?: boolean
  canClose?: boolean
}

const SidePanelContentContainer = ({ children, title, onClose, canClose }: any) => {
  const classes = useStyles({})

  return (
    <React.Fragment>
      <HeaderBar title={title} disableGutters className={classes.header}>
        {!!canClose &&
          <IconButton className={classes.closeButton} onClick={onClose} size="large">
            <Close />
          </IconButton>
        }
      </HeaderBar>
      <Box className={classes.sidePanelContent}>{children}</Box>
    </React.Fragment>
  );
}

export const SidePanelContent: React.FunctionComponent<SidePanelContentProps> = ({
  children,
  anchor,
  onClose,
  title,
  auditTitle,
  isReadyToAudit,
  isSupportPage,
  canClose = true,
}) => {
  const { dispatch: dispatchSidePanelAction } = useSidePanel()
  usePageViewAudit(auditTitle, !!isReadyToAudit, isSupportPage)

  React.useEffect(() => {
    dispatchSidePanelAction({
      type: 'OpenRightSidePanel',
    })

    return () => {
      dispatchSidePanelAction({
        type: 'CloseRightSidePanel',
      })
    }
  }, [])
  const target = usePortalTarget(SIDE_PANEL_CONTENT_IDS[anchor], 'div')

  return createPortal(
    <SidePanelContentContainer onClose={onClose} title={title} canClose={canClose}>
      {children}
    </SidePanelContentContainer>,
    target,
  )
}

SidePanelContent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.element,
    PropTypes.string,
    PropTypes.node,
  ]),
}
